"use client";

import { richText } from "@/components/language/hooks/richText";
import { useTranslation } from "@/components/language/hooks/useTranslation";
import { motion } from "framer-motion";
import Link from "next/link";

export default function SecurityPage() {
  const { t, tm } = useTranslation();

  const securityText = tm("security.features", []);
  const securityFeatures = [
    { icon: "🔐", color: "emerald" },
    { icon: "🎯", color: "blue" },
    { icon: "👤", color: "violet" },
    { icon: "🛡️", color: "orange" },
  ].map((item, i) => ({
    ...item,
    ...(securityText[i] as any),
  }));

  const protectionLayers = tm("security.protectionLayers");
  const tips = tm<string[]>("security.tips", []);
  const securityTips = [
    { icon: "🔒" },
    { icon: "📱" },
    { icon: "🌐" },
    { icon: "⚡" },
    { icon: "🔄" },
    { icon: "👀" },
  ];

  const trustFeatures = tm("security.trustFeatures", []);

  return (
    <div className="overflow-hidden text-white bg-brand-card">
      <section className="relative lg:min-h-screen overflow-hidden bg-brand-bg">

        {/* ── Desktop/large screens only: full-bleed background video ── */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1 }}
          className="hidden lg:block absolute inset-0 z-0 ml-[500px]"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-contain"
          >
            <source src="/videos/security-an.webm" type="video/webm" />
          </video>
        </motion.div>

        <div className="relative z-10 px-4 lg:px-0 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto flex flex-col items-center text-center lg:items-start lg:text-left justify-center lg:min-h-screen pt-24 sm:pt-28 lg:pt-0 pb-12 lg:pb-0 space-y-5 sm:space-y-6">

          <motion.h1
            initial={{ y: 30 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl lg:text-6xl xl:text-7xl 3xl:text-8xl font-bold leading-[1.1] tracking-tight"
          >
            <span className="text-brand-text">{t("security.hero.title1")}</span>
            <br />
            <span className="text-brand-purple">{t("security.hero.title2")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-2xl lg:mx-0 text-lg leading-relaxed text-brand-text 3xl:text-2xl"
          >
            {t("security.hero.description")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="grid sm:grid-cols-2 gap-2 justify-items-center lg:justify-items-start"
          >
            {trustFeatures.map((feature: any) => (
              <div key={feature.name} className="px-3 sm:px-4 py-1.5 rounded-full bg-brand-surface">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                  <span className="text-sm font-semibold text-brand-text whitespace-nowrap 3xl:text-2xl">{feature.name}</span>
                  <span className="hidden lg:inline text-xs text-brand-muted 3xl:text-xl">{feature.description}</span>
                </div>
              </div>
            ))}
          </motion.div>

          {/* ── Mobile/tablet only: video renders after all hero text, shown fully uncropped ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:hidden w-full pt-2"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="w-full h-auto block"
            >
              <source src="/videos/security-an-mobile.webm" type="video/webm" />
            </video>
          </motion.div>
        </div>
      </section>
      <section className="relative z-10 px-4 sm:px-6 sm:py-24 lg:py-32">
        <div className="mx-auto px-4 lg:px-0 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="mb-10 sm:mb-14 lg:mb-20 text-center"
          >
            <h2 className="mb-3 sm:mb-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold 3xl:text-[5rem] mt-5">
              {richText(t("security.layersTitle"), {
                brand: (chunks) => (
                  <span key={1} className="text-brand-text">
                    {chunks}
                  </span>
                ),
              })}
            </h2>
            <p className="max-w-2xl mx-auto text-base lg:text-xl text-brand-text/80 3xl:text-2xl">
              {t("security.layersDesc")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.25 } }}
                className="relative group"
              >
                <div className="relative h-full p-5 sm:p-7 lg:p-8 overflow-hidden bg-slate-900/50 backdrop-blur-sm global-border-radius lg:rounded-3xl">
                  <div className={`absolute inset-0 bg-brand-bg group-hover:to-transparent transition-all duration-500 global-border-radius lg:rounded-3xl`} />
                  <div className="relative z-10 space-y-3 sm:space-y-5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-3xl sm:text-4xl lg:text-5xl grayscale">{feature.icon}</div>
                      <div className="text-right">
                        <div className='text-xl sm:text-2xl lg:text-3xl font-bold text-brand-text'>{feature.stat}</div>
                        <div className="text-[10px] sm:text-xs tracking-wider uppercase text-slate-500">Security Level</div>
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-brand-text 3xl:text-[2rem]">{feature.title}</h3>
                    <p className="text-xs sm:text-sm lg:text-base leading-relaxed text-brand-text 3xl:text-2xl">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="relative z-10 px-4 sm:px-6 sm:py-24 lg:py-32 bg-brand-bg/80">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="mb-10 sm:mb-14 lg:mb-20 text-center"
          >
            <h2 className="mb-3 sm:mb-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold 3xl:text-[5rem]">
              {t("security.protectTitle1")}{" "} {t("security.protectTitle2")}
            </h2>
            <p className="max-w-2xl mx-auto text-sm sm:text-base lg:text-xl text-brand-text/80 3xl:text-2xl">
              {t("security.protectDesc")}
            </p>
          </motion.div>

          <div className="space-y-3 sm:space-y-5">
            {protectionLayers.map((layer: any, index: number) => (
              <motion.div
                key={layer.layer}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
              >
                <div className={`relative bg-brand-card global-border-radius sm:global-border-radius p-4 sm:p-6 lg:p-8 overflow-hidden`}>
                  <div className="flex flex-row items-center gap-4 sm:gap-6">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 global-border-radius sm:global-border-radius bg-brand-grey">
                        <span className="text-lg sm:text-2xl lg:text-3xl font-bold text-brand-bg">{layer.layer}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
                      <h3 className="text-base sm:text-xl lg:text-2xl font-bold text-brand-text 3xl:text-[2rem]">{layer.title}</h3>
                      <p className="text-xs sm:text-sm lg:text-base leading-relaxed text-brand-text 3xl:text-2xl">
                        {layer.description.includes("<")
                          ? richText(layer.description, {
                              link: (children: React.ReactNode) => (
                                <Link key={1} href="/contact-us" className="font-medium text-brand-warn hover:underline">
                                  {children}
                                </Link>
                              ),
                            })
                          : layer.description}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full bg-brand-grey">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-brand-bg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="relative z-10 px-4 sm:px-6 py-20 sm:py-24 lg:py-32 bg-brand-bg/80">
        <div className="px-4 lg:px-0 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="mb-10 sm:mb-14 lg:mb-20 text-center"
          >
            <h2 className="mb-3 sm:mb-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold 3xl:text-[5rem]">
              {richText(t("security.tipsTitle"), {
                support: (chunks) => (
                  <span key={1} className="text-brand-text">
                    {chunks}
                  </span>
                ),
              })}
            </h2>
            <p className="max-w-2xl mx-auto text-sm sm:text-base lg:text-xl text-brand-text/80 3xl:text-2xl">
              {t("security.tipsDesc")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {tips?.map((item, index) => {
              const icon = securityTips[index].icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <div className="h-full p-4 sm:p-5 lg:p-6 bg-brand-card backdrop-blur-sm global-border-radius sm:global-border-radius">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="flex-shrink-0 text-2xl sm:text-3xl grayscale">{icon}</div>
                      <p className="text-xs sm:text-sm lg:text-base leading-relaxed text-brand-text 3xl:text-2xl">{item}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative z-10 px-4 sm:px-6 py-20 sm:py-24 lg:py-32">
        <div className="px-4 lg:px-0 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="p-7 sm:p-10 lg:p-12 text-center border border-brand-text/10 global-border-radius sm:rounded-3xl bg-brand-bg/80"
          >
            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold 3xl:text-[2.5rem]">{t("security.ctaTitle")}</h2>
              <p className="max-w-xl mx-auto text-sm sm:text-base lg:text-lg text-brand-text 3xl:text-2xl">{t("security.ctaDesc")}</p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
                <a
                  href="mailto:security@vell.net"
                  className="px-6 sm:px-8 py-3 sm:py-4 font-semibold text-brand-card transition-colors duration-300 rounded-full bg-brand-text hover:bg-brand-text/90 text-sm sm:text-base 3xl:text-xl"
                >
                  {t("security.contactSecurity")}
                </a>
                <a
                  href="#"
                  className="px-6 sm:px-8 py-3 sm:py-4 font-semibold text-brand-text transition-colors duration-300 rounded-full border border-brand-text/20 hover:bg-brand-text/5 text-sm sm:text-base 3xl:text-xl"
                >
                  {t("security.viewReports")}
                </a>
              </div>
              <div className="pt-4 sm:pt-6">
                <p className="text-xs sm:text-sm text-brand-text 3xl:text-2xl">
                  {t("security.reportText")}{" "}
                  <a href="mailto:support@vell.net" className="font-semibold text-brand-text hover:underline">
                    support@vell.net
                  </a>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TRUST BADGES ── */}
      <section className="relative z-10 px-4 sm:px-6 py-12 sm:py-16 lg:py-20 bg-brand-card/80">
        <div className="mx-auto px-4 lg:px-0 lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {trustFeatures.map((cert: any, index: number) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col items-center gap-2 sm:gap-3 text-center"
              >
                <div className="flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-brand-grey">
                  <svg className="w-5 h-5 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-brand-card" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="text-xs sm:text-sm lg:text-base font-bold text-brand-text 3xl:text-2xl">{cert.name}</div>
                <div className="text-[10px] sm:text-xs text-brand-text/80 3xl:text-xl">{cert.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}